<script setup lang="ts">
import {CourseService, OrderService} from "@/api/api.ts";
import {ref, reactive} from "vue";
import {ElNotification} from "element-plus";
import {CourseApplyInfo, SimpleSection} from "@/types";
import {TimeFormatter} from "@/utils/common.ts";
import PageLabel from "components/common/PageLabel.vue";

const loadingRemote = ref(false);
const loading = ref(false);
const pagination = reactive({
  currentPage: 1,
  perPage: 10,
  total: 0
})
const applyList = ref<CourseApplyInfo[]>([])

const courseList = ref<SimpleSection[]>([])
// 用户名
const uContent = ref('')
// 手机号
const mContent = ref('')
// 订单号
const order = ref('')
// 课程ID
const courseId = ref()
/// 购买来源
const buySource = ref(0)
// 是否支付
const isPay = ref(0)
// 是否来自接口
const isApi = ref(-1)

const getApplyList = async () => {
  try {
    loading.value = true
    const res = await OrderService.getCourseApplyList({
      page: pagination.currentPage,
      pageSize: pagination.perPage,
      course_id: courseId.value,
      buy_source: buySource.value,
      u_content: uContent.value,
      m_content: mContent.value,
      is_pay: isPay.value,
      order: order.value,
      is_api: isApi.value === -1 ? undefined : isApi.value,
    })
    if (res.data.code === 200) {
      applyList.value = res.data.data?.list || []
      pagination.perPage = res.data.data?.pageSize || 10
      pagination.currentPage = res.data.data?.page || 1
      pagination.total = res.data.data?.count || 1
    } else {
      ElNotification.error({
        title: '获取报名列表失败',
        message: res.data.msg,
        type: 'error'
      })
    }
  } catch (err) {
    console.warn(err)
  } finally {
    loading.value = false
  }
}

const remoteMethod = (query: string) => {
  if (query) {
    getCourseList(query)
  } else {
    courseList.value = []
  }
}

const getCourseList = async (title: string) => {
  try {
    loadingRemote.value = true
    const res = await CourseService.getCourseSectionList({
      title: title,
    })
    if (res.data.code === 200) {
      courseList.value = [{id: 0, title: "全部课程"}, ...res.data.data!!]
    } else {
      ElNotification.error({
        title: '获取课程列表失败',
        message: res.data.msg,
        type: 'error'
      })
    }
  } catch (err) {
    console.warn(err)
  } finally {
    loadingRemote.value = false
  }
}

const onPageChange = (page: number) => {
  pagination.currentPage = page
  getApplyList()
}

const onSizeChange = (pageSize: number) => {
  pagination.perPage = pageSize
  getApplyList()
}

const onSearch = () => {
  pagination.currentPage = 1
  getApplyList()
}

getApplyList()
</script>

<template>
  <div class="affix-container mx-3rem mt-2.25rem">
    <page-label>报名中心</page-label>
<!--    <el-affix target=".affix-container" :offset="0">-->
      <div class="flex items-center bg-white p-1rem flex-wrap  gap-1rem mt-1rem rounded-md shadow-md">
        <el-input v-model="uContent" style="width: 12rem" placeholder="用户昵称" @change="onSearch"></el-input>
        <el-input v-model="mContent" style="width: 12rem" placeholder="用户手机号" @change="onSearch"></el-input>
        <el-input v-model="order" style="width: 12rem" placeholder="订单号" @change="onSearch"></el-input>
        <el-select
            v-model="courseId"
            placeholder="请输入课程标题"
            filterable remote remote-show-suffix :remote-method="remoteMethod"
            :loading="loadingRemote" style="width: 12rem" @change="onSearch">
          <el-option
              v-for="item in courseList"
              :key="item.id"
              :label="item.title"
              :value="item.id"
          />
        </el-select>
        <el-select v-model="buySource" style="width: 12rem" @change="onSearch">
          <el-option label="购买来源" :value="0"></el-option>
          <el-option label="用户购买" :value="1"></el-option>
          <el-option label="老师添加" :value="2"></el-option>
          <el-option label="系统添加" :value="3"></el-option>
        </el-select>
        <el-select v-model="isPay" style="width: 12rem" @change="onSearch">
          <el-option label="是否免费" :value="0"></el-option>
          <el-option label="付费课" :value="1"></el-option>
          <el-option label="免费课" :value="2"></el-option>
        </el-select>
        <el-select v-model="isApi" style="width: 12rem" @change="onSearch">
          <el-option label="是否来自接口" :value="-1"></el-option>
          <el-option label="是" :value="1"></el-option>
          <el-option label="否" :value="0"></el-option>
        </el-select>
        <el-button type="primary" @click="onSearch">查询</el-button>
      </div>
<!--    </el-affix>-->
    <div class="p-1rem mt-1rem bg-white rounded-md shadow-md">
      <el-table :data="applyList" v-loading="loading">
        <el-table-column label="ID" prop="id" fixed></el-table-column>
        <el-table-column label="用户ID" prop="user_id"></el-table-column>
        <el-table-column label="用户昵称" prop="user_name" width="150"></el-table-column>
        <el-table-column label="用户手机号" prop="mobile" width="150"></el-table-column>
        <el-table-column label="课程ID" prop="course_id"></el-table-column>
        <el-table-column label="课程标题" width="200" prop="title"></el-table-column>
        <el-table-column label="报名来源" prop="buy_source">
          <template #default="scope">
            {{
              scope.row.buy_source === 1 ? '用户购买' :
                  scope.row.buy_source === 2 ? '老师添加' :
                      scope.row.buy_source === 3 ? `系统添加` : ''
            }}
          </template>
        </el-table-column>
        <el-table-column label="是否来源于系列课" width="150" prop="is_alone">
          <template #default="scope">
            {{ scope.row.is_alone === 1 ? '是' : '否' }}
          </template>
        </el-table-column>
        <el-table-column label="订单号" prop="order" width="200"></el-table-column>
        <el-table-column label="支付金额" prop="price"></el-table-column>
        <el-table-column label="下单时间" width="200" prop="create_time">
          <template #default="scope">
            {{ TimeFormatter.formatTime(scope.row.create_time * 1000) }}
          </template>
        </el-table-column>
        <el-table-column label="支付时间" width="200" prop="pay_time">
          <template #default="scope">
            {{ TimeFormatter.formatTime(scope.row.pay_time * 1000) }}
          </template>
        </el-table-column>
        <el-table-column label="观看开始时间" width="200" prop="view_start_time">
          <template #default="scope">
            {{ TimeFormatter.formatTime(scope.row.view_start_time * 1000) }}
          </template>
        </el-table-column>
        <el-table-column label="最近学习时间" width="200" prop="lately_study_time">
          <template #default="scope">
            {{ TimeFormatter.formatTime(scope.row.lately_study_time * 1000) }}
          </template>
        </el-table-column>
        <el-table-column label="是否来自于接口" width="200" prop="is_api">
          <template #default="scope">
            {{ scope.row.is_api === 1 ? '是' : '否' }}
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pagination.perPage"
          :current-page="pagination.currentPage"
          :total="pagination.total"
          @current-change="onPageChange"
          @size-change="onSizeChange"
          layout="prev, pager, next, jumper, ->, total, sizes"
          class="mt-1rem"/>
    </div>
  </div>
</template>

<style scoped>
</style>